import { useEffect } from 'react';

export const useSmoothScroll = () => {
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href^="#"]') as HTMLAnchorElement;
      
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href || href === '#' || href === '') return;

      const targetElement = document.querySelector(href);
      if (!targetElement) return;

      e.preventDefault();

      const scrollToUpdatedTarget = () => {
        const updatedTarget = document.querySelector(href) as HTMLElement | null;
        if (!updatedTarget) return;

        const offset = window.innerWidth <= 768 ? 0 : 80;
        const documentTop = updatedTarget.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top: documentTop - offset,
          behavior: 'smooth'
        });
      };

      const scheduleScroll = () => {
        if (typeof window.requestAnimationFrame !== 'function') {
          scrollToUpdatedTarget();
          return;
        }
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(scrollToUpdatedTarget);
        });
      };

      const activeAccordion = document.querySelector('.accordion-item.active');
      const closingContent = targetElement.classList.contains('accordion-item')
        && activeAccordion
        && activeAccordion !== targetElement
        ? activeAccordion.querySelector('.accordion-content')
        : null;

      if (closingContent) {
        let fallbackId: number | undefined;
        let completed = false;

        const finishAfterCollapse = () => {
          if (completed) return;
          completed = true;
          closingContent.removeEventListener('transitionend', handleTransitionEnd);
          closingContent.removeEventListener('transitioncancel', handleTransitionEnd);
          if (fallbackId !== undefined) window.clearTimeout(fallbackId);
          scheduleScroll();
        };

        const handleTransitionEnd = (event: Event) => {
          const transitionEvent = event as TransitionEvent;
          if (event.target === closingContent && transitionEvent.propertyName === 'max-height') {
            finishAfterCollapse();
          }
        };

        closingContent.addEventListener('transitionend', handleTransitionEnd);
        closingContent.addEventListener('transitioncancel', handleTransitionEnd);
        fallbackId = window.setTimeout(finishAfterCollapse, 650);
      } else {
        scheduleScroll();
      }

      if (window.location.hash !== href) {
        window.history.pushState(null, '', href);
      }
      window.dispatchEvent(new Event('corazon:hash-navigation'));
    };

    document.addEventListener('click', handleClick);

    // Direct links (thecorazon.us/#wifi): the section may sit inside a folded
    // chapter or a closed accordion that only opens after the first render, so
    // the browser's own jump lands at the top. Scroll there ourselves once the
    // page has opened it, and again as photos above it finish loading — unless
    // the guest has started scrolling.
    const timers: number[] = [];
    let guestScrolled = false;
    const stopFollowing = () => { guestScrolled = true; };
    const initialHash = window.location.hash;
    if (initialHash && initialHash.length > 1) {
      if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
      window.addEventListener('wheel', stopFollowing, { passive: true, once: true });
      window.addEventListener('touchmove', stopFollowing, { passive: true, once: true });
      window.addEventListener('keydown', stopFollowing, { once: true });
      const jump = () => {
        if (guestScrolled) return;
        let target: HTMLElement | null = null;
        try {
          target = document.querySelector(initialHash) as HTMLElement | null;
        } catch {
          return;
        }
        if (!target || target.getBoundingClientRect().height === 0) return;
        const offset = window.innerWidth <= 768 ? 0 : 80;
        window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - offset, behavior: 'auto' });
      };
      [80, 400, 1000, 2000].forEach((ms) => timers.push(window.setTimeout(jump, ms)));
    }

    return () => {
      document.removeEventListener('click', handleClick);
      timers.forEach((t) => window.clearTimeout(t));
      window.removeEventListener('wheel', stopFollowing);
      window.removeEventListener('touchmove', stopFollowing);
      window.removeEventListener('keydown', stopFollowing);
    };
  }, []);
};
